import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, GitBranch, Trash2 } from 'lucide-react';
import { Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CreateTopicModal from './CreateTopicModal';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';

const BrainSettingConditions = ({ settingId, accountId, onRequirementsChange, initialRequirements = [] }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [requirements, setRequirements] = useState(initialRequirements);
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isAdding, setIsAdding] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState('');

  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);

  useEffect(() => {
    setRequirements(initialRequirements);
  }, [initialRequirements]);

  const fetchTopics = useCallback(async () => {
    if (!accountId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('brain_conversation_topics')
        .select('*')
        .eq('account_id', accountId)
        .order('topic_name');

      if (error) throw error;
      setTopics(data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Failed to load conversation topics.');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, supabase]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const handleAddRequirement = () => {
    if (!selectedTopicId || !selectedAnswer) {
      toast.error("Please select a topic and an answer.");
      return;
    }

    const topic = topics.find(t => t.id === selectedTopicId);
    if (!topic) return;

    // Prevent adding duplicate requirements
    const isDuplicate = requirements.some(
      req => req.topic_id === selectedTopicId && req.required_answer === selectedAnswer
    );
    if (isDuplicate) {
      toast.error("This qualifier has already been added.");
      return;
    }

    const newRequirement = {
      topic_id: selectedTopicId,
      required_answer: selectedAnswer,
      // For display purposes, add topic info
      brain_conversation_topics: {
        topic_name: topic.topic_name
      }
    };

    const updatedRequirements = [...requirements, newRequirement];
    setRequirements(updatedRequirements);
    onRequirementsChange(updatedRequirements);

    // Reset add form
    setIsAdding(false);
    setSelectedTopicId('');
    setSelectedAnswer('');
  };

  const handleRemoveRequirement = (index) => {
    const updatedRequirements = requirements.filter((_, i) => i !== index);
    setRequirements(updatedRequirements);
    onRequirementsChange(updatedRequirements);
  };

  const handleTopicCreated = (newTopic) => {
    // Add new topic to the list and select it
    setTopics(prev => [...prev, newTopic]);
    setSelectedTopicId(newTopic.id);
    setSelectedAnswer(''); // Reset answer selection
  };

  const selectedTopic = topics.find(t => t.id === selectedTopicId);

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.05 } }),
    exit: { opacity: 0, x: 10 }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 flex items-center">
          <Funnel size={14} className="mr-2 text-gray-500" />
          Qualifiers
        </h3>
        <p className="text-xs text-gray-400">
          {requirements.length} qualifier{requirements.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="min-h-[60px] bg-gray-50 rounded-xl p-3 border border-gray-200 space-y-2">
        <AnimatePresence>
          {requirements.length === 0 && !isAdding && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-4"
            >
              <p className="text-xs text-gray-500">No qualifiers set.</p>
              <p className="text-xs text-gray-400 mt-1">This will be provided regardless of user intent.</p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {requirements.map((req, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              layout
              className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center space-x-2 text-sm">
                <span className="font-medium text-gray-600">{req.brain_conversation_topics?.topic_name || 'Topic'}</span>
                <span className="text-gray-400">is</span>
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs font-medium border border-blue-100">{req.required_answer}</span>
              </div>
              <button onClick={() => handleRemoveRequirement(index)} className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50">
                <Trash2 size={14} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>

        {isAdding && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2 bg-white p-3 rounded-lg border border-gray-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select
                value={selectedTopicId}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    setIsTopicModalOpen(true);
                  } else {
                    setSelectedTopicId(e.target.value);
                    setSelectedAnswer('');
                  }
                }}
                className="w-full border border-gray-200 rounded-lg h-9 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-black/50"
              >
                <option value="" disabled>Select Qualifier</option>
                {topics.map(topic => (
                  <option key={topic.id} value={topic.id}>{topic.topic_name}</option>
                ))}
                <option value="new" className="text-blue-500 font-medium">＋ Create New Qualifier...</option>
              </select>

              <select
                value={selectedAnswer}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                disabled={!selectedTopicId}
                className="w-full border border-gray-200 rounded-lg h-9 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-black/50 disabled:bg-gray-100"
              >
                <option value="" disabled>Select Answer</option>
                {selectedTopic?.possible_answers?.map((ans, i) => (
                  <option key={i} value={ans}>{ans}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button onClick={() => setIsAdding(false)} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleAddRequirement} className="px-3 py-1.5 text-xs rounded-lg bg-black text-white">Add Qualifier</button>
            </div>
          </motion.div>
        )}
      </div>

      {!isAdding && (
        <button 
          onClick={() => setIsAdding(true)} 
          className="w-full flex items-center justify-center text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200/80 rounded-lg py-2 transition-colors border border-gray-200/80"
        >
          <Plus size={14} className="mr-1" />
          Add Qualifier
        </button>
      )}

      <CreateTopicModal 
        isOpen={isTopicModalOpen}
        onClose={() => setIsTopicModalOpen(false)}
        supabase={supabase}
        accountId={accountId}
        onSuccess={handleTopicCreated}
      />
    </div>
  );
};

export default BrainSettingConditions;
